import type { MuscleId } from './muscles';

/**
 * A "slot" is a job in a workout, not a specific exercise: "the heavy chest
 * press", "the side-delt isolation". Session templates are lists of slots, and
 * the generator fills each slot with the best exercise you actually have
 * equipment for. That indirection is what lets the app rotate your accessories
 * for variety while keeping your main lifts fixed so progression means
 * something.
 */
export type SlotTag =
  | 'chest_primary'
  | 'chest_secondary'
  | 'chest_isolation'
  | 'back_vertical'
  | 'back_horizontal'
  | 'back_isolation'
  | 'traps'
  | 'delts_press'
  | 'delts_side'
  | 'delts_rear'
  | 'biceps_primary'
  | 'biceps_secondary'
  | 'triceps_primary'
  | 'triceps_secondary'
  | 'forearms'
  | 'quad_primary'
  | 'quad_secondary'
  | 'quad_isolation'
  | 'ham_primary'
  | 'ham_isolation'
  | 'glutes'
  | 'calves'
  | 'adductors'
  | 'abs_flexion'
  | 'abs_antiextension'
  | 'obliques'
  | 'finisher';

export const SLOT_LABEL: Record<SlotTag, string> = {
  chest_primary: 'Heavy chest press',
  chest_secondary: 'Second chest press',
  chest_isolation: 'Chest isolation',
  back_vertical: 'Vertical pull',
  back_horizontal: 'Horizontal row',
  back_isolation: 'Lat isolation',
  traps: 'Traps',
  delts_press: 'Overhead press',
  delts_side: 'Side delts',
  delts_rear: 'Rear delts',
  biceps_primary: 'Heavy curl',
  biceps_secondary: 'Second curl',
  triceps_primary: 'Heavy triceps',
  triceps_secondary: 'Triceps isolation',
  forearms: 'Forearms',
  quad_primary: 'Heavy squat pattern',
  quad_secondary: 'Single-leg / second squat',
  quad_isolation: 'Quad isolation',
  ham_primary: 'Hip hinge',
  ham_isolation: 'Hamstring curl',
  glutes: 'Glutes',
  calves: 'Calves',
  adductors: 'Adductors',
  abs_flexion: 'Ab flexion',
  abs_antiextension: 'Anti-extension core',
  obliques: 'Obliques',
  finisher: 'Conditioning finisher',
};

export type MovementPattern =
  | 'horizontal_press'
  | 'incline_press'
  | 'vertical_press'
  | 'vertical_pull'
  | 'horizontal_pull'
  | 'squat'
  | 'hinge'
  | 'lunge'
  | 'isolation'
  | 'carry'
  | 'core';

export type LoadType =
  | 'barbell'
  | 'dumbbell'
  | 'machine'
  | 'cable'
  | 'bodyweight'
  | 'assisted'
  | 'timed';

export interface Exercise {
  id: string;
  name: string;
  /** Other names you might see on YouTube or on the machine's sticker. */
  aka?: string[];
  slots: SlotTag[];
  pattern: MovementPattern;
  primary: MuscleId[];
  secondary: MuscleId[];
  /** Every id here must be available for the exercise to be recommended. */
  equipment: string[];
  kind: 'compound' | 'isolation';
  loadType: LoadType;
  unilateral?: boolean;
  /** 1 = beginner-friendly, 2 = needs some practice, 3 = technical. */
  difficulty: 1 | 2 | 3;
  /**
   * How good this is at its job, 1–10. The generator prefers higher scores but
   * deliberately rotates so you don't do the same accessory for a year.
   */
  quality: number;
  defaultSets: number;
  repRange: [number, number];
  restSeconds: number;
  /** Smallest sensible jump in load, in kg. Machines and cables step coarser. */
  incrementKg: number;
  setup: string[];
  cues: string[];
  mistakes?: string[];
  /** Exercise ids to offer when the machine is taken or you want a change. */
  variations?: string[];
  /**
   * Visual descriptions used by scripts/generate-images.mjs to render the start
   * and end frame of the movement. Written as if describing a photo.
   */
  imagery: { start: string; end: string };
}

/** Where the generated illustrations live, relative to the app base URL. */
export function exerciseImage(id: string, frame: 'start' | 'end'): string {
  return `exercises/${id}-${frame}.jpg`;
}
